import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PaymentStepFrame from "../components/design/PaymentStepFrame";
import "../styles/registration.css";
import {
  estimateRegistrationAmount,
  isRegistrationSessionKey,
  parseRegistrationAmount,
  readRegistrationAmount,
  saveRegistrationAmount,
} from "../lib/registrationDraft";

function newSessionKey() {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    return (character === "x" ? random : (random & 3) | 8).toString(16);
  });
}

const won = (amount: number) => amount.toLocaleString("ko-KR");

export default function Registration() {
  const [params, setParams] = useSearchParams();
  const [newKey] = useState(newSessionKey);
  const requestedKey = params.get("session_key");
  const sessionKey = isRegistrationSessionKey(requestedKey) ? requestedKey : newKey;

  useEffect(() => {
    if (params.get("session_key") === sessionKey) return;
    const next = new URLSearchParams(params);
    next.set("session_key", sessionKey);
    setParams(next, { replace: true });
  }, [params, sessionKey, setParams]);

  return <RegistrationForm key={sessionKey} sessionKey={sessionKey} />;
}

function RegistrationForm({ sessionKey }: { sessionKey: string }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState(() => {
    try {
      const saved = readRegistrationAmount(localStorage, sessionKey);
      return saved === null ? "" : String(saved);
    } catch {
      return "";
    }
  });
  const [error, setError] = useState("");
  const validAmount = parseRegistrationAmount(amount);
  const estimate = estimateRegistrationAmount(amount);

  function continueRegistration(event: FormEvent) {
    event.preventDefault();
    if (validAmount === null) {
      setError("송금 금액은 1원부터 2,000만원까지 입력해 주세요.");
      input.current?.focus();
      return;
    }
    try {
      saveRegistrationAmount(localStorage, sessionKey, validAmount);
    } catch {
      setError("입력 내용을 저장하지 못했어요. 저장 공간 설정을 확인하고 다시 시도해 주세요.");
      return;
    }
    input.current?.blur();
    navigate(`/contracts/new?category=drink&session_key=${encodeURIComponent(sessionKey)}&entry=amount`);
  }

  return <div className="jp-registration">
    <PaymentStepFrame step={1} steps={["송금 금액", "정보·서류", "최종 확인"]} title="얼마를 보낼까요?" description="거래처에 보낼 주류대금을 입력해 주세요." onBack={() => navigate("/")}>
      <form className="jp-registration-form" onSubmit={continueRegistration} noValidate>
        <label className="jp-registration-amount">
          <span>송금 금액</span>
          <div>
            <input id="registration-amount" ref={input} type="text" inputMode="numeric" enterKeyHint="done" autoComplete="off" maxLength={16}
              aria-describedby={error ? "registration-amount-note registration-error" : "registration-amount-note"}
              aria-label="송금 금액" aria-invalid={!!error} placeholder="0" value={amount && Number.isSafeInteger(Number(amount)) ? won(Number(amount)) : amount}
              onChange={event => {
                const digits = event.target.value.replace(/,/g, "").trim();
                if (!/^\d*$/.test(digits)) { setError("원 단위의 숫자만 입력해 주세요."); return; }
                setAmount(digits);
                setError("");
              }}
              onKeyDown={event => {
                if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
              }}
            />
            <b>원</b>
          </div>
        </label>
        <div className="jp-registration-presets" role="group" aria-label="송금 금액 선택">
          {[500000, 1000000, 2000000].map(value => <button type="button" key={value} aria-pressed={validAmount === value} onClick={() => { setAmount(String(value)); setError(""); }}>{value / 10000}만원</button>)}
        </div>
        <p className="jp-registration-note" id="registration-amount-note">최대 2,000만원 · 이용료와 부가세 별도</p>
        <dl className="jp-registration-estimate" aria-live="polite" aria-atomic="true">
          <div><dt>이용료 3.7%</dt><dd>{won(estimate.fee)}원</dd></div>
          <div><dt>이용료 부가세</dt><dd>{won(estimate.vat)}원</dd></div>
          <div className="is-total"><dt>예상 카드결제 금액</dt><dd>{won(estimate.total)}원</dd></div>
        </dl>
        <p className="jp-registration-note">입금 일정과 분할결제는 다음 단계에서 선택해요.</p>
        {error && <p className="jp-registration-error" id="registration-error" role="alert">{error}</p>}
        <button className="jp-payment-primary jp-registration-next" type="submit">다음<ArrowRight size={18} aria-hidden="true" /></button>
      </form>
    </PaymentStepFrame>
  </div>;
}
